import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { userKeys } from "@/lib/query";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: Date;
}

/** Roles the admin UI can assign (Better Auth admin plugin: defaultRole "user", adminRoles ["admin"]). */
export type UserRole = "user" | "admin";

export function isUserRole(value: string): value is UserRole {
  return value === "user" || value === "admin";
}

export type { User };

export function useUsers(filters: { search?: string }) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async (): Promise<User[]> => {
      const { data, error } = await authClient.admin.listUsers({
        query: {
          limit: 100,
          ...(filters.search
            ? {
                searchField: "email" as const,
                searchValue: filters.search,
                searchOperator: "contains" as const,
              }
            : {}),
        },
      });
      if (error) throw error;
      return (data?.users as User[]) ?? [];
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      email: string;
      password: string;
      role: UserRole;
    }) => {
      const { error } = await authClient.admin.createUser(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create user");
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; role: UserRole }) => {
      const { error } = await authClient.admin.setRole(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to change role");
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.removeUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to delete user");
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const { error } = await authClient.admin.setUserPassword(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Password reset");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reset password");
    },
  });
}
